import { useEffect, useRef } from 'react';
import { isEditableTarget } from '@/lib/keyboard';

interface UseEscapeKeyOptions {
  /** Attach the listener to `window` instead of `document`. Default: `document`. */
  target?: 'document' | 'window';
  /** Skip Escape while focus is in a text field (input/textarea/contentEditable). */
  ignoreEditable?: boolean;
  /** Set `false` to detach the listener. Default: `true`. */
  enabled?: boolean;
}

/**
 * Run `onEscape` when the user presses Escape and no nearer handler already
 * consumed it. This is the guard that stops a *global* Escape action (navigate
 * away, close a panel) from also firing when Escape was meant to dismiss an
 * inner thing — see HW-727.
 *
 * It works because everything that legitimately consumes Escape runs *before*
 * this bubble-phase document/window listener, so `event.defaultPrevented` is
 * already set by the time we run:
 *   - our own inline-edit handlers call `preventDefault()` in the React bubble
 *     phase, dispatched at the React root before the event reaches document/window;
 *   - Radix dialogs/popovers (DismissableLayer) handle Escape in the *capture*
 *     phase and call `preventDefault()` before dismissing.
 *
 * The Radix part is an internal behaviour of the library, not a documented
 * contract: if a future Radix upgrade stopped calling `preventDefault()` on the
 * Escape it handles, guarded global actions here would start firing behind an
 * open dialog again. There is no runtime guard against that — only this note.
 */
export function useEscapeKey(
  onEscape: (event: KeyboardEvent) => void,
  { target = 'document', ignoreEditable = false, enabled = true }: UseEscapeKeyOptions = {},
) {
  // Keep the latest callback in a ref so the listener doesn't re-bind on every render.
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!enabled) return;
    const node: Document | Window = target === 'window' ? window : document;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (ignoreEditable && isEditableTarget(event.target)) return;
      onEscapeRef.current(event);
    };
    node.addEventListener('keydown', handleKeyDown as EventListener);
    return () => node.removeEventListener('keydown', handleKeyDown as EventListener);
  }, [target, ignoreEditable, enabled]);
}
